import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/api";
import { errorMessage } from "@/api/client";
import type { BoxInvite } from "@/api/types";
import { Button, ErrorBanner, PageHeader, Spinner } from "@/components/ui";
import { formatDateTime } from "@/lib/dates";

export default function BoxInvitesPage() {
  const qc = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState<BoxInvite | null>(null);
  const terms = useQuery({
    queryKey: ["box-terms", reviewing?.termsOfServiceId],
    queryFn: () => api.getBoxTerms(reviewing?.termsOfServiceId as string),
    enabled: Boolean(reviewing?.termsOfServiceId),
  });
  const acceptTerms = useMutation({
    mutationFn: (inv: BoxInvite) =>
      api.acceptBoxTerms(inv.termsOfServiceId as string),
    onSuccess: async (_, inv) => {
      setReviewing(null);
      await qc.invalidateQueries({ queryKey: ["box-invites"] });
      setNotice(`Terms accepted. You can now accept invite ${inv.id}.`);
    },
  });

  const invites = useQuery({
    queryKey: ["box-invites"],
    queryFn: api.listBoxInvites,
  });
  const accept = useMutation({
    mutationFn: (id: string) => api.acceptBoxInvite(id),
    onSuccess: (inv) => {
      setNotice(
        `Accepted invite ${inv.id}${inv.itemName ? ` (${inv.itemName})` : ""}.`,
      );
      return qc.invalidateQueries({ queryKey: ["box-invites"] });
    },
  });
  const err = [
    invites.error,
    accept.error,
    terms.error,
    acceptTerms.error,
  ].find(Boolean);

  return (
    <>
      <PageHeader
        title="Box Invites"
        actions={
          <Button
            variant="secondary"
            disabled={invites.isFetching}
            onClick={() => invites.refetch()}
          >
            Refresh
          </Button>
        }
      />
      <p className="mb-4 text-sm text-neutral-600">
        Folders shared with the Curse service account that are still pending.
        Accept an invite to give Curse access, then add the folder under Watched
        Folders. Box hides the folder name until the invite is accepted.
      </p>
      <ErrorBanner message={err ? errorMessage(err) : null} />
      {notice && (
        <p className="mb-4 rounded bg-green-50 p-3 text-sm text-green-800">
          {notice}
        </p>
      )}
      {invites.isPending ? (
        <Spinner />
      ) : (
        <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="bg-neutral-100 text-neutral-600">
              <tr>
                <th className="p-3">Folder</th>
                <th className="p-3">Role</th>
                <th className="p-3">Invited by</th>
                <th className="p-3">Invited</th>
                <th className="p-3">Invite ID</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {invites.data?.map((i) => (
                <tr key={i.id} className="border-t border-neutral-200">
                  <td className="p-3">
                    {i.itemName ?? "Hidden until accepted"}
                    {i.itemId && (
                      <span className="ml-2 font-mono text-neutral-500">
                        {i.itemId}
                      </span>
                    )}
                  </td>
                  <td className="p-3">{i.role}</td>
                  <td className="p-3">
                    {i.invitedByEmail ?? i.invitedBy ?? "—"}
                  </td>
                  <td className="p-3">{formatDateTime(i.createdAt)}</td>
                  <td className="p-3 font-mono">{i.id}</td>
                  <td className="flex justify-end p-3">
                    {i.termsOfServiceId ? (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setNotice(null);
                          setReviewing(i);
                        }}
                      >
                        Review terms
                      </Button>
                    ) : (
                      <Button
                        disabled={accept.isPending}
                        onClick={() => {
                          setNotice(null);
                          accept.mutate(i.id);
                        }}
                      >
                        Accept
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {invites.data?.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-neutral-500">
                    No pending invites.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {reviewing && (
        <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/40">
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-lg"
          >
            <h2 className="mb-2 text-lg font-semibold">
              Terms required to accept this invite
            </h2>
            <p className="mb-3 text-sm text-neutral-600">
              Accepting records agreement to these terms on behalf of the Curse
              service account
              {terms.data?.enterprise ? ` (${terms.data.enterprise})` : ""}.
              Only continue if you are authorized to agree to them.
            </p>
            {terms.isPending ? (
              <Spinner />
            ) : (
              <pre className="mb-4 max-h-80 overflow-auto whitespace-pre-wrap rounded border border-neutral-200 bg-neutral-50 p-3 text-xs">
                {terms.data?.text}
              </pre>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setReviewing(null)}>
                Cancel
              </Button>
              <Button
                disabled={!terms.data || acceptTerms.isPending}
                onClick={() => acceptTerms.mutate(reviewing)}
              >
                I agree on behalf of the service account
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
